// R35 — THE ONE WAY THIS HOUSE ASKS BEFORE IT DESTROYS SOMETHING OF HERS.
//
// JD's law, written for the journal and stated in its own comment: "ONE delete control, so the room
// panel and the drawer cannot drift apart in how they ask. Deletion is irreversible, so it always
// asks first, and it says what it is deleting rather than asking an abstract 'are you sure?'."
//
// IT WAS TRUE OF THE JOURNAL AND OF NOTHING ELSE. Measured at R35: two surfaces used this control,
// and FIVE MORE deleted a member's SAVED READING or her OWN SEAL on a single tap with no ask at all
// — the four report rows in My Room and the shelf drawer's card. A birth chart, a Human Design
// bodygraph, a tarot spread, a seal she paid a generation for, gone on a mis-tap, with no undo
// anywhere on this site. That is W12/R26's lesson exactly: the capability lived only on the surface
// that needed it first.
//
// TWO THINGS IT DECIDES, and both are the difference between a member losing work and not:
//
//   1. IT ALWAYS ASKS, AND THE SENTENCE NAMES THE KIND. `deleteAskConfirm` and `deleteAskLabel` are
//      DERIVED from the kind rather than typed at the call site, because a sentence typed at a call
//      site is held by nothing (R24: eighteen room names as literals). Seven call sites cannot now
//      ask in seven different voices, and a new surface gets the house's wording by existing.
//      `DELETE_NOUNS` is EXPORTED so a gate asks the TABLE per key rather than the returned string —
//      R28/R32's lesson, where a lookup's default collided with a legitimate answer and three real
//      types fell through it in production.
//
//   2. A DELETE THAT FAILED SAYS SO. `onDelete` returns a boolean and a `false` raises "That did not
//      delete. Try again." This is why the handlers behind it must not swallow their errors: before
//      R35, `deleteSavedReport` and the shelf's `remove` both caught the fetch, ignored it, and
//      dropped the row from her screen anyway — so a signed-out session or a database outage looked
//      byte-for-byte like a successful delete until she reloaded and the reading was still there.
//
// It moved out of App.tsx for the reason every roadmap-7 extraction has: `JournalEntryRow` is props
// in, tree out, no hook, so a gate CALLS it. `DeleteControl` holds state and came with it on R10-3's
// ruling — the stub jsx-runtime never calls a child, so the row stays fully callable and the gate
// can read that the control is THERE, wearing the right sentence, on a row that has one.
import { useState } from "react";
import { Trash2 } from "lucide-react";
import { JournalTitle } from "./JournalTitle";

// A journal entry as every list on this site reads one. The type moves with the component that
// renders it (the standing rule); App.tsx imports it back.
export type JournalEntry = {
  id: number | string;
  title: string;
  body: string;
  createdAt: string;
  // R57: TRUE only of an entry this house is holding in memory because the save was refused (or
  // there was no session to save it under). It is what stops a session-only row being drawn
  // byte-for-byte like a persisted one — which is exactly how a refused save used to look.
  unsaved?: boolean;
};

// WHAT THIS HOUSE CAN DESTROY, in the member's own words for each thing. There is no "item" in here
// on purpose: the fallback below is deliberately NOT one of these values, so a kind nobody
// registered reads as a fallthrough rather than as a legitimate answer that happens to match.
export const DELETE_NOUNS = {
  entry: "entry",                 // a journal entry she wrote
  reading: "reading",             // a saved report — birth chart, Human Design, tarot, palm, numerology
  seal: "seal",                   // a seal she generated in the Seal Creator
  // A saved person is the most expensive row on this site to lose: the server reads it, deletes it,
  // invalidates their rendered chart AND deletes the compatibility reading keyed to it. One tap used
  // to do all four.
  person: "saved person",
  conversation: "conversation",   // a whole Ask Lala thread, every turn of it paid for
  // Her broadcast composer's SAVE AS TEMPLATE (2026-08-14). A campaign she built block by block and
  // named; removing it destroys the shape and nothing else, and it is still hers.
  template: "template",
} as const;

export type DeleteKind = keyof typeof DELETE_NOUNS;

function nounFor(kind: DeleteKind) {
  return DELETE_NOUNS[kind] || "item";
}

// The screen-reader label names the SPECIFIC thing where there is a title to name (a shelf of eight
// readings needs eight distinguishable delete buttons), and falls back to the kind when there is not.
export function deleteAskLabel(kind: DeleteKind, what?: string) {
  return `Delete ${(what || "").trim() || `this ${nounFor(kind)}`}`;
}

// The confirm sentence names the KIND, not the title: it is read at the moment of deciding, where
// "Delete this reading?" is the question and the title is already on screen beside it.
export function deleteAskConfirm(kind: DeleteKind) {
  return `Delete this ${nounFor(kind)}? This can't be undone.`;
}

export function DeleteControl({ kind, what, onDelete, className = "" }: {
  kind: DeleteKind;
  what?: string;
  onDelete: () => Promise<boolean>;
  className?: string;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  if (confirming) {
    return (
      <span className={`delete-control delete-control--confirming ${className}`.trim()}>
        <span className="delete-control__ask">{deleteAskConfirm(kind)}</span>
        <button type="button" className="delete-control__no" onClick={() => setConfirming(false)} disabled={busy}>Keep</button>
        <button
          type="button"
          className="delete-control__yes"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            const ok = await onDelete();
            setBusy(false);
            if (!ok) { setFailed(true); setConfirming(false); }
          }}
        >
          {busy ? "Deleting..." : "Delete"}
        </button>
      </span>
    );
  }
  return (
    <span className={`delete-control ${className}`.trim()}>
      <button type="button" className="delete-control__open" onClick={() => { setFailed(false); setConfirming(true); }} aria-label={deleteAskLabel(kind, what)}>
        <Trash2 size={14} />
      </button>
      {failed ? <span className="delete-control__failed">That did not delete. Try again.</span> : null}
    </span>
  );
}

// R57 — **THE ONE PLACE THIS HOUSE SAYS A JOURNAL ENTRY IS ONLY IN THIS TAB.** Two lists draw one of
// her entries and both must wear the mark; a decision that has to hold on two surfaces is one
// component or it is two things that agree until somebody edits one of them (W12/R26/R35/R38 are the
// same finding four times). It is hookless, so a gate CALLS it — which the My Room row, holding a
// `useState`, can never be. The label is a PARAMETER with an English DEFAULT (the file imports
// nothing from the catalog), held to `journal.unsaved` from both sides by `scripts/r57-journal.mjs`.
export function JournalUnsavedMark({ entry, label = "Not saved" }: { entry: JournalEntry; label?: string }) {
  if (!entry?.unsaved) return null;
  return <span className="journal-unsaved">{label}</span>;
}

// X6: one row in the entries list to the LEFT of the pad. Click it to load that entry into the pad
// for editing; the active (currently-edited) one is lit. Delete stays, tucked on the row.
export function JournalEntryRow({ entry, active, onSelect, onDelete, unsavedLabel = "Not saved" }: {
  entry: JournalEntry;
  active: boolean;
  onSelect: (entry: JournalEntry) => void;
  onDelete: (id: string) => Promise<boolean>;
  // R57: the word for a row that never reached the database. It takes a DEFAULT rather than being
  // required (14-B7b's ruling) because two gates CALL this row with nothing, and the English default
  // is held against the catalog from both sides.
  unsavedLabel?: string;
}) {
  const d = new Date(entry.createdAt);
  const date = Number.isNaN(d.getTime()) ? "" : `${d.getMonth() + 1}/${d.getDate()}`;
  const preview = (entry.body || "").replace(/\s+/g, " ").trim().slice(0, 70);
  return (
    <li className={active ? "journal-entries__item journal-entries__item--active" : "journal-entries__item"}>
      <button type="button" className="journal-entries__open" onClick={() => onSelect(entry)} aria-current={active}>
        <span className="journal-entries__row">
          <JournalTitle title={entry.title} expanded={active} className="journal-entries__name" />
          <JournalUnsavedMark entry={entry} label={unsavedLabel} />
          {date ? <span className="journal-entries__date">{date}</span> : null}
        </span>
        {preview ? <span className="journal-entries__preview">{preview}</span> : null}
      </button>
      <DeleteControl kind="entry" what={entry.title} onDelete={() => onDelete(String(entry.id))} />
    </li>
  );
}

// J1: a My Room journal entry renders COLLAPSED to its title; click to expand the body, click again
// to close. A long entry never opens by default (that plus J2's column isolation is what stops one
// entry shoving the room around).
//
// R57 MOVED IT HERE, beside the OTHER row that draws one of her journal entries. It holds a hook, so
// a gate cannot CALL it — but the two rows now render the same unsaved mark, and a mark that has to
// appear on both lists is one decision when they sit in one file and two when they do not. That is
// the whole argument for the move: every capability this house has ever lost lived on one of two
// surfaces that should have agreed (W12/R26/R35/R38).
export function JournalPanelItem({ entry, onDelete, unsavedLabel = "Not saved" }: {
  entry: JournalEntry;
  onDelete: (id: string) => Promise<boolean>;
  unsavedLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <li className={open ? "journal-list__item journal-list__item--open" : "journal-list__item"}>
      <div className="journal-list__head">
        <button type="button" className="journal-list__toggle" onClick={() => setOpen(!open)} aria-expanded={open}>
          <span className="journal-list__chev" aria-hidden="true">{open ? "▾" : "▸"}</span>
          <JournalTitle title={entry.title} expanded={open} className="journal-list__title" />
        </button>
        <JournalUnsavedMark entry={entry} label={unsavedLabel} />
        <span className="journal-list__date">{new Date(entry.createdAt).toLocaleDateString()}</span>
        <DeleteControl kind="entry" what={entry.title} onDelete={() => onDelete(String(entry.id))} />
      </div>
      {open ? <p className="journal-list__body">{entry.body}</p> : null}
    </li>
  );
}
